/**
 * プライベートメールアドレス（渡す相手ごとに発行する使い捨て転送アドレス）の
 * 語彙・契約・純関数。
 *
 * ## 何のための機能か
 *
 * Apple「メールを非公開」/ Firefox Relay / DuckDuckGo Email Protection と同じ型。
 * 利用者は**サービスごとに違うランダムなアドレス**を発行し、本来のアドレスを
 * 渡さない。届いたメールは本来のメールボックスへ落ちる。
 *
 *   - `k7m2x9q4@example.jp` を「〇〇通販」に渡す
 *   - 迷惑メールが来たら**そのアドレスだけ**を止める（本来のアドレスは無傷）
 *   - 止めた瞬間に「**どのサービスが漏らしたか**」が確定する ← ここが主目的
 *
 * 最後の 1 点が価値の中心なので、**発行時にラベル（渡す相手）を必須**にする
 * （`privateAddressLabelSchema`）。ラベル無しで発行できると、届いた迷惑メールから
 * 漏洩元をたどれず「アドレスがたくさんある」だけの機能になる。
 *
 * ## アドレスの形
 *
 * `<random>@<契約者自身のドメイン>`。**共有ドメインは v1 では作らない**
 * （1 組織のスパムで全契約者のレピュテーションが落ちる。共有 IP と同じ事故を
 * ドメイン名でもう一度作ることになる）。自ドメインなので「その組織の人」で
 * あることは相手に分かるが、守るのは**本来のアドレス**であって組織の秘匿ではない。
 *
 * ## なぜ base32 から紛らわしい文字を除くのか
 *
 * 発行したアドレスは電話口で読み上げられ、紙のフォームに手書きされ、
 * 相手の担当者が目で写す。`l` と `1`、`0` と `O` を混ぜると、その場では
 * 気付かれずに**届かないアドレスが相手の台帳に残る**。使い捨てアドレスは
 * 「届かない」の原因を本人が調べにくい（自分のメールボックスには何も来ない）
 * ので、入口で潰しておく。
 *
 * ## このファイルに `node:crypto` を持ち込まない
 *
 * `@mail-sv/shared` は admin / portal（Vite でブラウザへバンドルされる）からも
 * import される。乱数は WebCrypto（`globalThis.crypto.getRandomValues`）を使う。
 * Node 18 以降とブラウザのどちらでもグローバルに存在する。
 */
import { z } from 'zod';
import { RESERVED_LOCAL_PARTS, isValidLocalPart } from './lib/validation.js';

/* ==================================================== 語彙 */

/**
 * 発行したアドレスの状態。
 *
 * `active`   … 受信できる。desired-state に載る。
 * `disabled` … 本人が止めた。**ノードの配送表から消えて 550 になる。**
 *              「静かに捨てる」にしない（正当な相手が送ったときに、届かなかった
 *              ことが相手にも本人にも分からなくなる）。
 * `burned`   … 迷惑メールが来たので焼却した。`disabled` と配送上の挙動は同じだが、
 *              **漏洩元の記録としての意味が違う**ので分ける。「使い終わったので
 *              止めた」と「漏らされたので捨てた」を同じ状態にすると、
 *              どのサービスが名簿を売ったかの統計が取れなくなる。
 */
export const PRIVATE_ADDRESS_STATUS = ['active', 'disabled', 'burned'] as const;
export type PrivateAddressStatus = (typeof PRIVATE_ADDRESS_STATUS)[number];

/** 発行経路。濫用は「総数」ではなく「どの経路でどれだけ増えたか」で見る。 */
export const PRIVATE_ADDRESS_CREATED_VIA = ['portal', 'api'] as const;
export type PrivateAddressCreatedVia = (typeof PRIVATE_ADDRESS_CREATED_VIA)[number];

/** 受信できる状態か（desired-state に載せる判定と画面の表示を揃えるため）。 */
export function isPrivateAddressDeliverable(status: string): boolean {
  return status === 'active';
}

/* ==================================================== desired-state への載せ方 */

/**
 * プライベートアドレスは **desired-state の `aliases` セクションに載せる**。
 *
 * ノード側（`stack/mailnode/sql/002_views.sql` の `postfix_aliases`）は
 * `aliases` 表を `virtual_alias_maps` として引くので、**エージェントにも
 * ノードにも 1 行の追加も要らない**。専用セクションを足すと、
 * `DESIRED_STATE_SECTIONS` の増加に合わせてエージェントの取得ループ・
 * ノード DB の表・世代掃除（`DELETE ... WHERE generation < …`）の 3 か所を
 * 同時に直す必要があり、片方だけ古いエージェントが居るあいだ
 * **転送だけが黙って消える**という直しにくい壊れ方をする。
 *
 * 無効化・焼却した行は**載せない**。載せないことがそのままノード DB からの
 * 削除（＝次の generation で `DELETE`）になり、宛先不明の 550 が返る。
 * 「静かに捨てる」中間状態を作らないための唯一の実装がこれ。
 *
 * id の衝突: エイリアス由来の行は `<aliasId>-<index|destId>` なので、
 * ここで付ける `-p` 接尾辞と混ざることはない（id の生成器が共通で、
 * そもそも別の行の id が一致しない）。
 */
export const PRIVATE_ADDRESS_DESIRED_ID_SUFFIX = '-p';

/** desired-state の `aliases` セクションで使う行 id。**両方の生成器で共有すること。** */
export function privateAddressDesiredAliasId(privateAddressId: string): string {
  return `${privateAddressId}${PRIVATE_ADDRESS_DESIRED_ID_SUFFIX}`;
}

/* ==================================================== ローカルパートの生成 */

/**
 * 生成に使う文字。**base32（RFC 4648）から紛らわしい文字を除いたもの。**
 *
 *   除いた文字: `i` `l` `o`（`1` `0` と紛れる）
 *   base32 に元から無い文字: `0` `1` `8` `9`
 *
 * 残り 29 文字。`isValidLocalPart()` を必ず通る文字だけで構成されている
 * （英小文字と数字のみ。先頭・末尾の制約にも自動的に適合する）。
 */
export const PRIVATE_ADDRESS_ALPHABET = 'abcdefghjkmnpqrstuvwxyz234567';

/**
 * 既定の長さ。29^10 ≈ 4.2×10^14 通り。
 *
 * 8 文字（29^8 ≈ 5×10^11）でも辞書攻撃には十分だが、**総当たりの相手は
 * スパマーの SMTP セッションであって暗号解読ではない**。1 アドレスあたりの
 * 試行に SMTP の往復が要るので、この空間なら現実的な時間で当たらない。
 * 一方で長すぎると読み上げ・手書きに耐えないため 10 で止める。
 */
export const PRIVATE_ADDRESS_LOCAL_PART_LENGTH = 10;
/** 下限。これより短い指定は受け付けない（総当たりに現実味が出る）。 */
export const PRIVATE_ADDRESS_LOCAL_PART_MIN = 8;
export const PRIVATE_ADDRESS_LOCAL_PART_MAX = 24;

/** ラベル（渡す相手）の最大長。DB の CHECK（0051）と同じ値。 */
export const PRIVATE_ADDRESS_LABEL_MAX = 120;
/** メモの最大長。 */
export const PRIVATE_ADDRESS_NOTE_MAX = 500;

/** 乱数の供給元。テストから決定的な列を差し込めるように引数で受ける。 */
export type RandomBytesFn = (length: number) => Uint8Array;

function webCryptoRandomBytes(length: number): Uint8Array {
  const target = new Uint8Array(length);
  const webcrypto = (globalThis as { crypto?: { getRandomValues?: (a: Uint8Array) => Uint8Array } })
    .crypto;
  if (!webcrypto?.getRandomValues) {
    // ここに落ちる環境では **Math.random にフォールバックしない**。
    // 予測できる乱数で発行したアドレスは、総当たり以前に列挙できてしまう。
    throw new Error('WebCrypto が利用できないため、プライベートアドレスを発行できません');
  }
  webcrypto.getRandomValues(target);
  return target;
}

/**
 * ランダムなローカルパートを 1 つ作る。
 *
 * **剰余で丸めない。** `byte % 29` にすると 0〜27 の文字が 9/256、28 の文字が
 * 8/256 になり、わずかだが均一でなくなる。使い捨てアドレスは「推測できない」
 * ことが唯一の防御なので、偏りは作らずに**棄却抽出**する
 * （256 を 29 で割った余りに当たるバイトは捨てて引き直す）。
 *
 * 予約ローカルパート（`postmaster` / `abuse` …）と一致する列は返さない。
 * 現実には長さが違うのでまず当たらないが、**生成器が予約語を出さないことを
 * 呼び出し側が確かめなくてよい**状態にしておく（呼び忘れが起きない形にする）。
 */
export function generatePrivateLocalPart(
  length: number = PRIVATE_ADDRESS_LOCAL_PART_LENGTH,
  randomBytes: RandomBytesFn = webCryptoRandomBytes,
): string {
  if (
    !Number.isInteger(length) ||
    length < PRIVATE_ADDRESS_LOCAL_PART_MIN ||
    length > PRIVATE_ADDRESS_LOCAL_PART_MAX
  ) {
    throw new Error(
      `プライベートアドレスの長さは ${PRIVATE_ADDRESS_LOCAL_PART_MIN}〜${PRIVATE_ADDRESS_LOCAL_PART_MAX} 文字で指定してください`,
    );
  }

  const alphabet = PRIVATE_ADDRESS_ALPHABET;
  // 256 のうち、この値以上のバイトは捨てる（余りの偏りを持ち込まないため）。
  const ceiling = 256 - (256 % alphabet.length);

  for (let attempt = 0; attempt < 8; attempt += 1) {
    const out: string[] = [];
    // 棄却で目減りするぶんを見込んで多めに引く。足りなければ引き直す。
    // **引き直しの回数に上限を置く。** 上限が無いと、乱数源が
    // 「棄却対象のバイトしか返さない」壊れ方をしたときに**無限ループ**になり、
    // 発行 API が 500 も返さずリクエストごとに 1 コアを焼き続ける。
    for (let draw = 0; draw < 16 && out.length < length; draw += 1) {
      const bytes = randomBytes((length - out.length) * 2 + 8);
      for (const byte of bytes) {
        if (byte >= ceiling) continue;
        out.push(alphabet[byte % alphabet.length] as string);
        if (out.length === length) break;
      }
    }
    if (out.length < length) continue;
    const candidate = out.join('');
    if (isReservedForPrivateAddress(candidate)) continue;
    if (!isValidLocalPart(candidate)) continue;
    return candidate;
  }
  // ここに来るのは乱数源が壊れている（定数を返している / 棄却対象しか返さない）とき。
  // **黙って弱い値を返さずに落とす。**
  throw new Error('プライベートアドレスのローカルパートを生成できませんでした');
}

/**
 * 予約されているローカルパートか。
 *
 * RFC 2142 の必須窓口（`postmaster` / `abuse` …）に加えて、**バウンス処理と
 * 購読解除の受け口**も除く。これらはノード側で特別扱いされる名前で、
 * 使い捨てアドレスに割り当てると DSN や解除リンクの経路が壊れる。
 */
export function isReservedForPrivateAddress(localPart: string): boolean {
  const value = localPart.trim().toLowerCase();
  if (RESERVED_LOCAL_PARTS.has(value)) return true;
  return PRIVATE_ADDRESS_EXTRA_RESERVED.has(value);
}

const PRIVATE_ADDRESS_EXTRA_RESERVED: ReadonlySet<string> = new Set([
  'bounce',
  'bounces',
  'unsubscribe',
  'no-reply',
  'noreply',
  'root',
  'admin',
  'support',
  'info',
]);

/* ==================================================== aurora-mail の identity */

/**
 * Webメール（aurora-mail）側の identity 表示名に付ける印。
 *
 * ## なぜ表示名で印を付けるのか
 *
 * 向こうの `identities` に「どこから来たか」を持てる列が無い
 * （`aurora-mail` は参照専用なので列を足せない）。同期は
 * **「こちらが作ったものだけを消す」**必要があるので、
 * 利用者が自分で作った identity と見分ける手掛かりが要る。
 * 表示名はそのまま受信一覧・compose の From セレクタに出るため、
 * **印がそのまま利用者への説明になる**（設計メモ §8 推奨 3）。
 *
 * 逆に言えば、利用者が手で同じ名前を付けると同期の対象と見なされて
 * 消える。実害は「自分で作った From 候補が消える」ことだが、
 * 消す前に必ず**アドレスがこちらの発行済み一覧に無いこと**も確かめるので、
 * 「本来のアドレスの identity が消える」ことは起こらない。
 */
export const PRIVATE_ADDRESS_IDENTITY_SUFFIX = '（非公開アドレス）';

/** 表示名。**ラベルを頭に置く**（どのサービス用かが一覧で真っ先に読める）。 */
export function privateAddressIdentityName(label: string): string {
  // 向こうの `name` は最大 120 文字。印のぶんを差し引いてラベルを切る。
  const room = 120 - PRIVATE_ADDRESS_IDENTITY_SUFFIX.length;
  const trimmed = label.trim().slice(0, Math.max(1, room));
  return `${trimmed}${PRIVATE_ADDRESS_IDENTITY_SUFFIX}`;
}

/** mail-sv が同期した identity か（＝消してよい対象か）の判定。 */
export function isPrivateAddressIdentityName(name: string): boolean {
  return name.endsWith(PRIVATE_ADDRESS_IDENTITY_SUFFIX);
}

/* ==================================================== 契約（zod） */

/**
 * ラベル。**必須で、空白だけの文字列も拒否する。**
 * `.trim()` を通したうえで最小 1 文字を要求するので、`"   "` は 400 になる。
 */
export const privateAddressLabelSchema = z
  .string()
  .trim()
  .min(1, '渡す相手（ラベル）を入力してください')
  .max(PRIVATE_ADDRESS_LABEL_MAX);

export const privateAddressStatusSchema = z.enum(PRIVATE_ADDRESS_STATUS);

export const privateAddressCreateSchema = z.object({
  /** 届いたメールの行き先。省略できない（どこへ落とすかは推測しない）。 */
  mailboxId: z.string().min(1),
  /** 省略時はメールボックスと同じドメイン。 */
  domainId: z.string().min(1).optional(),
  label: privateAddressLabelSchema,
  note: z.string().max(PRIVATE_ADDRESS_NOTE_MAX).optional(),
  /**
   * ローカルパートの長さ。**指定できるのは長さだけで、値そのものは指定できない。**
   * 好きな文字列を取れるなら `aliases` と同じものになり、
   * 「推測できない」という前提が利用者の命名習慣で崩れる。
   */
  localPartLength: z
    .number()
    .int()
    .min(PRIVATE_ADDRESS_LOCAL_PART_MIN)
    .max(PRIVATE_ADDRESS_LOCAL_PART_MAX)
    .optional(),
});
export type PrivateAddressCreateInput = z.infer<typeof privateAddressCreateSchema>;

/**
 * 更新できるのは**ラベルとメモだけ**。
 * アドレスそのものを付け替えられると、相手に渡した紙と食い違う。
 * 状態は専用の口（`/disable` `/enable`）で変える（理由を必ず添えるため）。
 */
export const privateAddressUpdateSchema = z
  .object({
    label: privateAddressLabelSchema.optional(),
    note: z.string().max(PRIVATE_ADDRESS_NOTE_MAX).nullable().optional(),
  })
  .refine((v) => v.label !== undefined || v.note !== undefined, {
    message: '変更する項目を 1 つ以上指定してください',
  });
export type PrivateAddressUpdateInput = z.infer<typeof privateAddressUpdateSchema>;

/**
 * 無効化。`burn = true` なら `burned`（漏らされたので捨てた）にする。
 * 理由は任意だが、**入れておくと「どのサービスが漏らしたか」の一次情報になる**。
 */
export const privateAddressDisableSchema = z.object({
  reason: z.string().max(200).optional(),
  burn: z.boolean().default(false),
});
export type PrivateAddressDisableInput = z.infer<typeof privateAddressDisableSchema>;

/** 一覧の絞り込み。ラベルは部分一致（渡した相手の名前で探すため）。 */
export const privateAddressFilterSchema = z.object({
  status: privateAddressStatusSchema.optional(),
  label: z.string().trim().min(1).max(PRIVATE_ADDRESS_LABEL_MAX).optional(),
  mailboxId: z.string().min(1).optional(),
});
export type PrivateAddressFilter = z.infer<typeof privateAddressFilterSchema>;

/* ==================================================== 表示用 */

export interface PrivateAddressView {
  id: string;
  address: string;
  localPart: string;
  domainId: string;
  mailboxId: string;
  mailboxAddress: string;
  label: string;
  note: string | null;
  status: PrivateAddressStatus;
  disabledAt: string | null;
  disabledReason: string | null;
  receivedCount: number;
  spamCount: number;
  lastReceivedAt: string | null;
  createdVia: PrivateAddressCreatedVia;
  createdAt: string;
}

/**
 * 一覧に添える統計。**「発行数」だけでは濫用も漏洩も分からない。**
 * 焼却率（`burned / total`）と迷惑メール率（`spam / received`）が、
 * 「どのサービスが漏らしたか」と「この組織は使い捨てを乱発していないか」の
 * 両方を見るための最小の指標になる。
 */
export interface PrivateAddressSummary {
  total: number;
  active: number;
  disabled: number;
  burned: number;
  receivedTotal: number;
  spamTotal: number;
  /** プラン上限（0 = この機能を使えない）。 */
  limit: number;
  /** あと何個作れるか。上限 0 なら 0。 */
  remaining: number;
}

/**
 * あと何個作れるか。**上限 0 は「無制限」ではなく「使えない」**
 * （プランの他の上限は 0 = 無制限だが、この機能だけは 0 を「機能なし」に使う。
 * `planFeaturesSchema` にフラグを足して 2 か所で同じことを表さないため）。
 */
export function privateAddressesRemaining(limit: number, current: number): number {
  if (limit <= 0) return 0;
  return Math.max(0, limit - current);
}

/** プランでこの機能が使えるか。 */
export function isPrivateAddressEnabled(limit: number): boolean {
  return limit > 0;
}
